import React, { useEffect, useState } from "react";
import Dashboard from "../../../services/api/dashboard";

function ExamReportData() {
  const [reportData, setReportData] = useState([]);
  const [intermediateCount, setIntermediateCount] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ✅ Fetch Yesterday + Today data
  const fetchIntermediateCountData = async () => {
    try {
      const response = await Dashboard.getIntermediateCount();
      console.log("Intermediate Count Data:", response);

      if (response?.data && Array.isArray(response.data)) {
        setIntermediateCount(response.data);
      } else {
        setError("No intermediate count data available");
      }
    } catch (err) {
      console.error("Error fetching intermediate count data:", err);
      // setError("Failed to load intermediate count");
    }
  };

  // ✅ Fetch Area + Division Data
  const fetchReportData = async () => {
    try {
      const response = await Dashboard.getExamReportData();
      console.log("Exam Report Data:", response);

      if (response?.data && Array.isArray(response.data)) {
        const sortedData = response.data.sort((a, b) =>
          a.areaName.localeCompare(b.areaName)
        );
        setReportData(sortedData);
      } else {
        // setError("No report data available");
      }
    } catch (err) {
      console.error("Error fetching report data:", err);
      // setError("Failed to load report");
    }
  };

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      await Promise.all([fetchIntermediateCountData(), fetchReportData()]);
      setLoading(false);
    };
    fetchAll();
  }, []);

  if (loading)
    return <div className="text-center mt-5 text-lg">Loading...</div>;
  if (error)
    return <div className="text-center text-red-600 mt-5 text-lg">{error}</div>;

  // ✅ Calculate total for today = yesterday + today
  // ✅ Show "today" as (today + yesterday)
  let todayTotal = 0;
  let yesterdayTotal = 0;
  let summaryRows = [];

  if (intermediateCount.length > 0) {
    // assuming API returns sorted by date desc or asc — we'll handle both
    const sorted = [...intermediateCount].sort(
      (a, b) => new Date(a.appliedDate) - new Date(b.appliedDate)
    );

    if (sorted.length >= 2) {
      yesterdayTotal = sorted[0].totalApplications;
      todayTotal = sorted[1].totalApplications + sorted[0].totalApplications;
    } else if (sorted.length === 1) {
      todayTotal = sorted[0].totalApplications;
    }

    // ✅ Build the summary rows for table
    summaryRows = sorted.map((dto, index) => {
      const isToday = index === sorted.length - 1;
      return {
        date: new Date(dto.appliedDate).toLocaleDateString("en-US", {
          month: "short",
          day: "2-digit",
          year: "numeric",
        }),
        total: isToday ? todayTotal : yesterdayTotal,
      };
    });
  } else {
    summaryRows = [];
  }

  // ✅ Calculate division totals
  const calculateTotals = () =>
    reportData.reduce(
      (acc, item) => ({
        md: acc.md + (item.md || 0),
        wd: acc.wd + (item.wd || 0),
        ymd: acc.ymd + (item.ymd || 0),
        ywd: acc.ywd + (item.ywd || 0),
        fd: acc.fd + (item.fd || 0),
      }),
      { md: 0, wd: 0, ymd: 0, ywd: 0, fd: 0 }
    );

  const totals = calculateTotals();
  const grandTotal =
    totals.md + totals.wd + totals.ymd + totals.ywd + totals.fd;

  const calculateRowTotal = (item) =>
    (item.md || 0) +
    (item.wd || 0) +
    (item.ymd || 0) +
    (item.ywd || 0) +
    (item.fd || 0);

  // ✅ Common style
  const thTdStyle = {
    border: "1px solid #000",
    padding: "8px 10px",
    textAlign: "center",
  };

  return (
    <div className="flex flex-col items-center mt-6 space-y-8">
      {/* ---------- Intermediate Summary Table ---------- */}
      <div
        className="bg-white shadow-md rounded-lg overflow-hidden w-11/12 lg:w-8/12"
        style={{ border: "1px solid #000" }}
      >
        <h3
          className="text-center text-lg font-semibold py-3 bg-gray-100"
          style={{ marginLeft: "1%" }}
        >
          Intermediate Exams Enrollment Summary
        </h3>

        <table
          className="w-full text-sm text-gray-900"
          style={{ borderCollapse: "collapse", width: "100%" }}
        >
          <thead style={{ backgroundColor: "#e5e7eb", fontWeight: "600" }}>
            <tr>
              {/* Empty header cell (for first column) with accessible label */}
              <th aria-label="Label column" style={thTdStyle} />

              {/* Dynamic date headers */}
              {summaryRows.map((row) => (
                <th key={row.date} style={thTdStyle}>
                  {row.date}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            <tr>
              {/* First cell label */}
              <td
                style={{
                  ...thTdStyle,
                  textAlign: "left",
                  fontWeight: "bold",
                }}
              >
                Intermediate Exam Registrations
              </td>

              {/* Data cells */}
              {summaryRows.map((row) => {
                const isToday =
                  row.date === summaryRows[summaryRows.length - 1].date;
                return (
                  <td
                    key={row.date}
                    style={{
                      ...thTdStyle,
                      backgroundColor: isToday ? "#f3f4f6" : "#ffffff",
                      fontWeight: isToday ? "bold" : "normal",
                    }}
                  >
                    {row.total.toLocaleString()}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      {/* ---------- Area & Division Table ---------- */}
      <div
        className="bg-white shadow-md rounded-lg overflow-hidden w-11/12 lg:w-8/12"
        style={{ border: "1px solid #000" }}
      >
        <h3
          className="text-center text-lg font-semibold py-3 bg-gray-100"
          style={{ marginLeft: "1%" }}
        >
          Intermediate Exam Enrollments - Area and Division wise (in descending
          order of number of enrollments)
        </h3>

        <table
          className="w-full text-sm text-gray-900"
          style={{ borderCollapse: "collapse", width: "100%" }}
        >
          <thead style={{ backgroundColor: "#e5e7eb", fontWeight: "600" }}>
            <tr>
              <th style={thTdStyle}>S.No.</th>
              <th style={thTdStyle}>Area</th>
              <th style={thTdStyle}>MD</th>
              <th style={thTdStyle}>WD</th>
              <th style={thTdStyle}>YMD</th>
              <th style={thTdStyle}>YWD</th>
              <th style={thTdStyle}>FD</th>
              <th style={thTdStyle}>Total</th>
            </tr>
          </thead>

          <tbody>
            {reportData.map((item, index) => (
              <tr
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                style={{
                  backgroundColor: index % 2 === 0 ? "#fff" : "#f9fafb",
                }}
              >
                <td style={thTdStyle}>{index + 1}</td>
                <td style={thTdStyle}>{item.areaName}</td>
                <td style={thTdStyle}>{item.md?.toLocaleString() ?? 0}</td>
                <td style={thTdStyle}>{item.wd?.toLocaleString() ?? 0}</td>
                <td style={thTdStyle}>{item.ymd?.toLocaleString() ?? 0}</td>
                <td style={thTdStyle}>{item.ywd?.toLocaleString() ?? 0}</td>
                <td style={thTdStyle}>{item.fd?.toLocaleString() ?? 0}</td>
                <td style={{ ...thTdStyle, fontWeight: "600" }}>
                  {calculateRowTotal(item).toLocaleString()}
                </td>
              </tr>
            ))}

            <tr style={{ backgroundColor: "#e5e7eb", fontWeight: "bold" }}>
              <td colSpan="2" style={thTdStyle}>
                Total
              </td>
              <td style={thTdStyle}>{totals.md.toLocaleString()}</td>
              <td style={thTdStyle}>{totals.wd.toLocaleString()}</td>
              <td style={thTdStyle}>{totals.ymd.toLocaleString()}</td>
              <td style={thTdStyle}>{totals.ywd.toLocaleString()}</td>
              <td style={thTdStyle}>{totals.fd.toLocaleString()}</td>
              <td style={thTdStyle}>{grandTotal.toLocaleString()}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ExamReportData;
